import { z } from 'zod';
import { posDdtDocumentSerialSchema } from './ddt-identifiers';

/**
 * Goods-receipt scanner resolution contract.
 *
 * Identity ≠ allocation: the scanned code identifies a physical piece (EAN,
 * supplier code or serial); the DDT line it is credited to is decided by the
 * client model (FIFO over the residual lines the server lists here, minus the
 * quantities already verified locally). The server never allocates.
 */
const scanUuid = z.string().uuid();

export const posGoodsReceiptScanResolveInputSchema = z.object({
  code: z.string().trim().min(1).max(255),
  /** DDT passivi selected for this receipt; residual lines are searched only here. */
  ddt_ids: z.array(scanUuid).min(1).max(100),
  /** Optional DDT the scanner is restricted to ("Scanner su: solo DDT N"). */
  focus_ddt_id: scanUuid.nullable().optional(),
}).strict().superRefine((value, ctx) => {
  if (value.focus_ddt_id && !value.ddt_ids.includes(value.focus_ddt_id)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Il DDT di focus deve essere tra i DDT selezionati.', path: ['focus_ddt_id'] });
  }
});
export type PosGoodsReceiptScanResolveInput = z.infer<typeof posGoodsReceiptScanResolveInputSchema>;

/** A residual DDT line candidate, already ordered FIFO (DDT date, number, line). */
export const posGoodsReceiptScanCandidateSchema = z.object({
  ddt_id: scanUuid,
  ddt_number: z.string(),
  ddt_date: z.string().nullable(),
  item_id: scanUuid,
  product_id: scanUuid,
  /** Always null: variants are retired from the receipt UX; kept for contract parity. */
  variant_id: scanUuid.nullable(),
  serial_mode: z.enum(['none', 'unique', 'non_unique']),
  quantity_remaining: z.coerce.number().nonnegative(),
}).strict();
export type PosGoodsReceiptScanCandidate = z.infer<typeof posGoodsReceiptScanCandidateSchema>;

const scanProductSchema = z.object({
  id: scanUuid,
  name: z.string(),
  sku: z.string().nullable(),
  serial_mode: z.enum(['none', 'unique', 'non_unique']),
}).strict();

export const posGoodsReceiptScanResolveResultSchema = z.discriminatedUnion('kind', [
  /** Serial declared on a selected DDT line: the only true 1:1 imputation. */
  z.object({
    kind: z.literal('declared_serial'),
    serial: posDdtDocumentSerialSchema,
    candidate: posGoodsReceiptScanCandidateSchema,
    product: scanProductSchema,
  }).strict(),
  /**
   * Serial already registered in the warehouse (any unit status): the uniqueness
   * indexes forbid a new inbound unit, so it is rejected before insertion.
   */
  z.object({
    kind: z.literal('serial_in_stock'),
    serial_value: z.string(),
    status: z.string(),
    product: scanProductSchema.nullable(),
  }).strict(),
  /** EAN or unambiguous supplier code identifying one article on the residual lines. */
  z.object({
    kind: z.literal('article'),
    identity: z.enum(['ean', 'supplier_code']),
    product: scanProductSchema,
    candidates: z.array(posGoodsReceiptScanCandidateSchema).min(1),
  }).strict(),
  /** Supplier code shared by several residual articles: the operator must scan the box EAN. */
  z.object({
    kind: z.literal('ambiguous_supplier_code'),
    products: z.array(scanProductSchema).min(2),
  }).strict(),
  /** Serial-looking code not declared and not in stock: serialized residual lines only. */
  z.object({
    kind: z.literal('unknown_serial'),
    serial_value: z.string(),
    candidates: z.array(posGoodsReceiptScanCandidateSchema).min(1),
  }).strict(),
  /** Nothing on the selected DDTs matches this code. */
  z.object({
    kind: z.literal('not_found'),
  }).strict(),
]);
export type PosGoodsReceiptScanResolveResult = z.infer<typeof posGoodsReceiptScanResolveResultSchema>;

/** Permission that authorizes accepting DDT-declared serials without scanning them. */
export const GOODS_RECEIPT_TRUST_DECLARED_SERIALS_PERMISSION = 'goods_receipt.trust_declared_serials';

/** Receipt-line marker written by the server when declared serials were accepted without scanning. */
export const GOODS_RECEIPT_TRUSTED_SERIALS_NOTE = 'Seriali accreditati dal DDT senza scansione';

/**
 * FIFO ordering of scanner candidates: DDT date, then DDT number, then the
 * server-provided line order. Shared so client allocation and server listing
 * agree on the same sequence.
 */
export function compareGoodsReceiptScanCandidates(
  left: Pick<PosGoodsReceiptScanCandidate, 'ddt_date' | 'ddt_number'>,
  right: Pick<PosGoodsReceiptScanCandidate, 'ddt_date' | 'ddt_number'>,
): number {
  const leftDate = left.ddt_date ?? '9999-12-31';
  const rightDate = right.ddt_date ?? '9999-12-31';
  if (leftDate !== rightDate) return leftDate < rightDate ? -1 : 1;
  return left.ddt_number.localeCompare(right.ddt_number, 'it', { numeric: true });
}
